import { CacheEntry, HttpOptions } from "./types.js";
import { Log } from "./log.js";

export const DEFAULT_OPTIONS: HttpOptions = {
    dataType: "application/json",
    contentType: "application/json",
    timeout: 2000
}

const CACHE_DELAY = 1; // seconds

export class Http {

    // Properties
    public options: HttpOptions;
    private static number = 0;

    // Event handling methods
    public onError(response: any): void {}
    public onResponse(response: any): void {}
    public onTimeout(timeout: number): void {}

    constructor(options?: HttpOptions) {
        this.options = {
            ...DEFAULT_OPTIONS,
            ...options
        };
    }

    public async request(method: "GET" | "POST" | "PUT" | "DELETE", url: string, options?: HttpOptions): Promise<any> {
        // Merge global options with request options
        options = {
            ...this.options,
            ...options
        };

        // Ajax API
        const id = Http.number++;

        // Make a call
        Log.d(`Http(#${id}): ${method.toUpperCase()} ${url}`);

        // Response result
        let response: Response = null;

        // Crete header
        let headers = new Headers();
        headers.set("Accept", options.dataType);

        // Json expected when not form data
        if (!options.form) {
            headers.set("Content-Type", "application/json");
        }

        // Do request
        try {
            response = await fetch(url, {
                method: method,
                mode: options.mode || "cors",
                cache: "no-cache",
                credentials: "same-origin",
                headers: headers,
                redirect: "follow",
                referrerPolicy: "no-referrer",
                body: options.form || JSON.stringify(options.data)
            });
        }
        catch (e) {
            Log.d(`Http(#${id}): Exception`);

            // Response text
            let text = await response?.text();

            // OnError handler
            this.onError({
                status: response?.status,
                response: text
            });

            throw {
                status: response?.status,
                response: text
            };
        }

        // OnResponse handler
        this.onResponse(response);

        // HTTP status 300 or 400?
        if (response.status >= 400) {
            Log.d(`Http(#${id}): Error`);

            // Response json
            let json = await response.json();

            // OnError handler
            this.onError({
                status: response.status,
                response: json
            });

            throw {
                status: response?.status,
                response: json
            };
        }

        // No content?
        if (response.status == 204 || response.headers.get("content-length") == "0") {
            return null;
        }
        // JSON response?
        else if (response.headers.get("content-type")?.lastIndexOf("application/json") > -1) {
            return await response.json();
        }
        // Unknown response type?
        else {
            Log.w(`Http(#${id}): Missing Content-Type header. Trying to parse as JSON first.`);

            // Unknown content type, will try to parse JSON first
            try {
                return await response.json();
            }
            catch (e) {
                return await response.text();
            }
        }
    }
}
